import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Image, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import ImageSelector from "./guests/components/form/ImageSelector";
import { FormField } from "./guests/components/form/FormFields";
import { CreateRoomFormData, createRoomSchema } from "./utils/roomSchema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import RoomStatusField from "./rooms/components/form/StatusField";
import { useHotel } from "@/hooks/useHotel";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useRoomCreate } from "./rooms/hooks/useRoomCreate";
import { useRoomCategoriesFiltersSettings } from "@/hooks/useRoomCategories";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RoomStatus } from "@/types/room";
import { useRooms } from "@/hooks/useRooms";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { isLimitReached } from "@/utils/accessControl";

export const AddRoomPage = () => {
  const { t } = useTranslation();
  const { isLoading, error } = useHotel();
  const hotelId = useCurrentHotelId();

  if (isLoading && !hotelId) {
    return <div>{t("reservation.roomsForm.loading")}</div>;
  }

  if (error) {
    return (
      <div className="py-4 text-center text-red-600">
        {t("reservation.roomsForm.hotelLoadError", {
          message: error.message,
        })}
      </div>
    );
  }

  if (!hotelId) {
    return <div>{t("reservation.roomsForm.loading")}</div>;
  }

  return <AddRoom hotelId={hotelId} />;
};

const AddRoom = ({ hotelId }: { hotelId: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);

  const { handleRoomSubmit, isLoading } = useRoomCreate();
  const {
    limits: { roomsLimit },
  } = useAccessCapabilities();
  const { data: settings, isLoading: isLoadingRoomCategoriesSettings } =
    useRoomCategoriesFiltersSettings(hotelId!);
  const { data: roomsResponse } = useRooms(hotelId, 1, 1, undefined, !!hotelId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<CreateRoomFormData>({
    resolver: zodResolver(createRoomSchema),
  });

  const selectedImage = watch("photoId");
  const status = watch("status");
  const categoryId = watch("categoryId");
  const totalRooms = roomsResponse?.total ?? 0;
  const roomLimitReached = isLimitReached(totalRooms, roomsLimit);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const onSubmit = async (data: CreateRoomFormData) => {
    if (roomLimitReached) {
      return;
    }

    await handleRoomSubmit(data, hotelId!, selectedPhotoFile);
  };

  if (isLoadingRoomCategoriesSettings) {
    return <div>{t("reservation.roomsForm.loading")}</div>;
  }

  return (
    <div className="relative min-h-screen p-6">
      <Button
        variant="new_outline"
        size="sm"
        onClick={() => navigate("/reservations/rooms")}
        className="absolute top-12 left-12"
      >
        <ArrowLeft />
        {t("reservation.roomsForm.back")}
      </Button>

      <div className="mx-auto mt-12 max-w-4xl">
        <h1 className="mb-4 text-center text-2xl font-semibold">
          {t("reservation.roomsForm.title")}
        </h1>
        {roomLimitReached ? (
          <p className="text-muted-foreground mb-4 text-center text-sm">
            {t("reservation.roomsForm.roomLimitReached", {
              limit: roomsLimit,
            })}
          </p>
        ) : null}
        <div>
          <ImageSelector
            defaultIcon={<Image className={"size-[72px] stroke-1"} />}
            overlayContent={
              <div
                className={
                  "absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white"
                }
              >
                <PlusCircle className={"size-5"} />
              </div>
            }
            onImageSelect={handleImageUpload}
            selectedImage={selectedImage || undefined}
            className="mb-4"
            imageClassName={"size-[72px] rounded-full"}
          />
          {errors.photoId?.message && (
            <span className="text-center text-red-600">
              {t("reservation.roomsForm.photoRequired")}
            </span>
          )}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-8 flex flex-col gap-6">
            <FormField
              id="number"
              label={t("reservation.roomsForm.number")}
              type="number"
              required
              register={register}
              error={errors.number?.message}
              placeholder={t("reservation.roomsForm.numberPlaceholder")}
            />

            <FormField
              id="floor"
              label={t("reservation.roomsForm.floor")}
              type="number"
              required
              register={register}
              error={errors.floor?.message}
              placeholder={t("reservation.roomsForm.floorPlaceholder")}
            />

            <FormField
              id="area"
              label={t("reservation.roomsForm.area")}
              type="number"
              required
              register={register}
              error={errors.area?.message}
              placeholder={t("reservation.roomsForm.areaPlaceholder")}
            />

            <RoomStatusField
              value={status}
              onChange={(value) => setValue("status", value as RoomStatus)}
              error={errors.status?.message}
              withOccupiedStatuses={false}
            />

            <div>
              <Label htmlFor="room-category">
                {t("reservation.roomsForm.category")}
              </Label>
              <Select
                value={categoryId}
                onValueChange={(value) => setValue("categoryId", value)}
              >
                <SelectTrigger
                  id="room-category"
                  className="w-full justify-between"
                  aria-invalid={!!errors.categoryId?.message}
                >
                  <SelectValue
                    placeholder={t(
                      "reservation.roomsForm.categoriesPlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all categories">
                      {t("reservation.roomsForm.categoriesPlaceholder")}
                    </SelectItem>
                    {settings?.categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              variant="new_default"
              size="xl"
              disabled={isSubmitting || isLoading || roomLimitReached}
            >
              {isSubmitting
                ? t("reservation.roomsForm.creating")
                : t("reservation.roomsForm.create")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRoomPage;
